import { Link } from "react-router-dom";
import { MapPin, Users, Compass, Clock, Award, MessageCircle, ArrowRight, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useSite } from "../lib/SiteContext";
import { useEnquiry } from "../lib/EnquiryContext";
import { waLink } from "../lib/api";
import { optImg } from "../lib/image";
import Seo from "../components/Seo";
import FadeIn from "../components/FadeIn";
import TourCard from "../components/TourCard";

const ICONS = { MapPin, Users, Compass, Clock, Award, MessageCircle };

const Home = () => {
  const { content, tours } = useSite();
  const { openEnquiry } = useEnquiry();
  if (!content) return <div className="min-h-screen" data-testid="home-loading" />;

  const featured = tours.filter((t) => t.featured);
  const featuredTours = (featured.length ? featured : tours).slice(0, 6);
  const marquee = ["Paleokastritsa", "Corfu Old Town", "Canal d'Amour", "Porto Timoni", "Kassiopi", "Mount Pantokrator", "Mouse Island", "Pelekas", "Afionas", "Achilleion Palace"];

  return (
    <div data-testid="home-page">
      <Seo
        title="Aloha Corfu Tours — Private Tours & Experiences in Corfu, Greece"
        description="Discover Corfu with fully private tours: breathtaking beaches, charming villages, historic landmarks and hidden gems. Local Corfiot knowledge, your pace. Prices on request."
      />

      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="relative h-[78svh] min-h-[520px] w-full overflow-hidden md:h-[86vh]">
          <img src={optImg(content.hero_image, 1920, 80)} alt="Turquoise coastline of Corfu, Greece — private tours with Aloha Corfu Tours"
               className="h-full w-full object-cover" fetchpriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-r from-aegean/80 via-aegean/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-aegean/50 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
              <FadeIn>
                <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-seafoam-light backdrop-blur">
                  <Sparkles className="h-3.5 w-3.5 text-gold" /> Private Tours · Corfu, Greece
                </p>
                <h1 className="max-w-2xl text-balance font-display text-4xl font-semibold leading-[1.05] text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
                  {content.hero_title}
                </h1>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg" data-testid="hero-subtitle">
                  {content.hero_subtitle}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to="/tours" data-testid="hero-explore-tours-button"
                        className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-pine shadow-lift transition-transform duration-200 hover:scale-[1.03]">
                    Explore Our Tours <ArrowRight className="h-4 w-4" />
                  </Link>
                  <button onClick={() => openEnquiry()} data-testid="hero-request-tour-button"
                          className="rounded-full bg-pine px-6 py-3 text-sm font-semibold text-white ring-1 ring-white/30 transition-colors duration-200 hover:bg-pine-light">
                    Request a Tour
                  </button>
                  <Link to="/customize" data-testid="hero-customize-button"
                        className="rounded-full bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur ring-1 ring-white/30 transition-colors duration-200 hover:bg-white/20">
                    Customize Your Experience
                  </Link>
                  <a href={waLink(content.settings)} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button"
                     className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03]">
                    <MessageCircle className="h-4 w-4" /> Contact Us on WhatsApp
                  </a>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
        {/* marquee */}
        <div className="border-y border-sand-dark bg-sand py-3 overflow-hidden" aria-hidden="true">
          <div className="animate-marquee flex w-max gap-10">
            {[...marquee, ...marquee].map((d, i) => (
              <span key={i} className="flex items-center gap-10 whitespace-nowrap font-display text-sm italic text-pine/70">
                {d} <span className="text-gold">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TOURS */}
      <section className="py-16 md:py-24" data-testid="featured-tours-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Handpicked for you</p>
              <h2 className="mt-2 font-display text-3xl font-semibold text-pine sm:text-4xl">Featured Tours</h2>
            </div>
            <Link to="/tours" className="inline-flex items-center gap-1.5 text-sm font-semibold text-pine hover:underline underline-offset-4" data-testid="view-all-tours-link">
              View all tours <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {featuredTours.map((tour, i) => (
              <FadeIn key={tour.id} delay={i * 0.08}><TourCard tour={tour} /></FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="bg-seafoam-light/60 py-16 md:py-24" data-testid="destinations-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-10 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Where we go</p>
            <h2 className="mt-2 font-display text-3xl font-semibold text-pine sm:text-4xl">Popular Destinations in Corfu</h2>
          </FadeIn>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
            {content.destinations.map((d, i) => (
              <FadeIn key={d.name} delay={i * 0.08}>
                <Link to="/tours" data-testid={`destination-card-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group relative block aspect-[3/4] overflow-hidden rounded-2xl shadow-soft">
                  <img src={optImg(d.image, 700)} alt={`${d.name}, Corfu — ${d.blurb}`} loading="lazy" decoding="async"
                       className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-aegean/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 p-4">
                    <h3 className="font-display text-lg font-semibold text-white">{d.name}</h3>
                    <p className="mt-0.5 text-xs text-white/75">{d.blurb}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 md:py-24" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-10 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">{content.about_title}</p>
            <h2 className="mt-2 font-display text-3xl font-semibold text-pine sm:text-4xl">The Aloha way of exploring</h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600">{content.about_text}</p>
          </FadeIn>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {content.why_us.map((item, i) => {
              const Icon = ICONS[item.icon] || Compass;
              return (
                <FadeIn key={item.title} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-gray-100 bg-white p-6 shadow-soft transition-shadow duration-300 hover:shadow-lift">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-seafoam-light">
                      <Icon className="h-5 w-5 text-pine" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold text-pine">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-500">{item.text}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOM TOURS */}
      <section className="bg-pine-dark py-16 md:py-24" data-testid="custom-tours-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">Personalized tours</p>
            <h2 className="mt-2 font-display text-3xl font-semibold text-white sm:text-4xl">{content.custom_title}</h2>
            <p className="mt-4 text-base leading-relaxed text-white/75">{content.custom_text}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/customize" data-testid="custom-section-button"
                    className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-pine transition-transform duration-200 hover:scale-[1.03]">
                Customize Your Experience
              </Link>
              <button onClick={() => openEnquiry("", "custom")} data-testid="custom-section-enquire"
                      className="rounded-full bg-white/10 px-6 py-3 text-sm font-semibold text-white ring-1 ring-white/30 transition-colors hover:bg-white/20">
                Send us your wishes
              </button>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="relative">
              <img src={optImg("https://images.unsplash.com/photo-1515086215594-3427c60130a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", 1000)}
                   alt="Sunset over the Ionian Sea on a private Corfu boat experience" loading="lazy" decoding="async"
                   className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lift" />
              <div className="absolute -bottom-5 -left-5 hidden rounded-2xl bg-white p-5 shadow-lift sm:block">
                <p className="font-display text-2xl font-semibold text-pine">100% private</p>
                <p className="text-xs text-gray-500">Your group, your pace, your day</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-16 md:py-24" data-testid="gallery-preview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">The island, in frames</p>
              <h2 className="mt-2 font-display text-3xl font-semibold text-pine sm:text-4xl">Corfu Photo Gallery</h2>
            </div>
            <Link to="/gallery" className="inline-flex items-center gap-1.5 text-sm font-semibold text-pine hover:underline underline-offset-4" data-testid="view-gallery-link">
              View full gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {content.gallery.slice(0, 6).map((src, i) => (
              <FadeIn key={src} delay={i * 0.05} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <img src={optImg(src, 800)} alt={`Corfu scenery — Aloha Corfu Tours gallery photo ${i + 1}`} loading="lazy" decoding="async"
                     className={`w-full rounded-2xl object-cover shadow-soft transition-transform duration-500 hover:scale-[1.02] ${i === 0 ? "aspect-square md:h-full" : "aspect-square"}`} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="bg-sand py-16 md:py-24" data-testid="faq-preview-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Good to know</p>
            <h2 className="mt-2 font-display text-3xl font-semibold text-pine sm:text-4xl">Frequently Asked Questions</h2>
          </FadeIn>
          <FadeIn>
            <Accordion type="single" collapsible className="rounded-2xl border border-gray-100 bg-white px-5 shadow-soft">
              {content.faqs.slice(0, 5).map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-semibold text-pine" data-testid={`home-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
          <FadeIn className="mt-6 text-center">
            <Link to="/faq" className="text-sm font-semibold text-pine hover:underline underline-offset-4" data-testid="view-all-faq-link">
              Read all questions →
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-16 md:py-24" data-testid="final-cta-section">
        <img src={optImg("https://images.unsplash.com/photo-1633013980214-95bbfe6427df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", 1600, 80)}
             alt="Corfu coastline at golden hour" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-aegean/70" />
        <FadeIn className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">Ready to discover the real Corfu?</h2>
          <p className="mt-4 text-base text-white/80">
            Tell us your dates and wishes — we reply personally with availability and a no-obligation quote.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={() => openEnquiry()} data-testid="final-cta-enquire-button"
                    className="rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-pine transition-transform duration-200 hover:scale-[1.03]">
              Request a Tour
            </button>
            <a href={waLink(content.settings)} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp-button"
               className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03]">
              <MessageCircle className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default Home;
